import { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Grid,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { Button, Card, LoadingSpinner, ErrorMessage, AddressAutocomplete, PhoneField } from "@/components/ui";
import { DEFAULT_PHONE_DIAL_CODE } from "@/constants/phoneDialCodes";
import { formatLocationLine } from "@/utils/location";
import { isAddressDeliverable } from "@/utils/addressDeliverability";
import {
  customerService,
  CreateCustomerData,
  CreateCustomerAddressData,
  type CustomerAddress,
} from "@/services/customer.service";
import { enterpriseService } from "@/services/enterprise.service";
import { allergenService } from "@/services/allergen.service";
import { useToastStore } from "@/store/toastStore";

const customerSchema = z.object({
  firstName: z
    .string()
    .max(255, "Le prénom est trop long")
    .optional()
    .nullable()
    .transform((v) => (v == null ? null : v.trim() || null)),
  lastName: z
    .string()
    .min(1, "Le nom est obligatoire")
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(255, "Le nom est trop long"),
  type: z.enum(["INSTITUTION", "COMPANY", "INDIVIDUAL"], { required_error: "Le type de client est obligatoire" }),
  enterpriseId: z.string().optional(),
  indice: z.union([z.string(), z.number()]).optional().nullable().transform((v) => (v === "" || v === undefined || v === null ? undefined : Number(String(v)))),
  phoneDialCode: z.string().default(DEFAULT_PHONE_DIAL_CODE),
  phone: z.string().optional(),
  email: z.string().email("Email invalide").optional().or(z.literal("")),
  contactPerson: z.string().optional(),
  notes: z.string().optional(),
  discountAmount: z.union([z.string(), z.number()]).optional().transform((v) => (v === "" || v === undefined ? undefined : Number(String(v).replace(",", ".")))),
  defaultPaymentMode: z.enum(["EN_COMPTE", "COMPTANT"]).optional().nullable(),
  isActive: z.boolean().default(true),
  takesStarter: z.boolean().optional(),
  takesMain: z.boolean().optional(),
  takesDessert: z.boolean().optional(),
  proteinPreference: z.enum(["FISH", "MEAT", "BOTH", "VEGETARIAN"]).optional().nullable(),
  prefersPremium: z.boolean().optional(),
  prefersVegetarian: z.boolean().optional(),
  allergenIds: z.array(z.string()).optional().default([]),
});

type CustomerFormData = z.infer<typeof customerSchema>;

const addressFormSchema = z.object({
  label: z.string().optional(),
  address: z.string().min(1, "L'adresse est requise"),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  latitude: z.number().optional().nullable(),
  longitude: z.number().optional().nullable(),
  isDefault: z.boolean().default(false),
  isBilling: z.boolean().default(false),
});
type AddressFormData = z.infer<typeof addressFormSchema>;

/**
 * Page de formulaire pour créer ou éditer un client
 */
type CustomerFormLocationState = { fromOrder?: boolean; suggestedName?: string };

export function CustomerFormPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEditing = !!id;
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  const [addressForm, setAddressForm] = useState<AddressFormData>({
    label: "",
    address: "",
    postalCode: "",
    city: "",
    latitude: null as number | null,
    longitude: null as number | null,
    isDefault: false,
    isBilling: false,
  });
  const [addressFormErrors, setAddressFormErrors] = useState<Partial<Record<keyof AddressFormData, string>>>({});

  // Récupérer les entreprises
  const { data: enterprises = [] } = useQuery({
    queryKey: ["enterprises"],
    queryFn: () => enterpriseService.getAll(),
  });

  // Récupérer les allergènes (référentiel)
  const { data: allergens = [] } = useQuery({
    queryKey: ["allergens"],
    queryFn: () => allergenService.getAll(),
  });

  // Récupérer le client si édition
  const {
    data: customer,
    isLoading: customerLoading,
    error: customerError,
  } = useQuery({
    queryKey: ["customers", id],
    queryFn: () => customerService.getById(id!),
    enabled: isEditing,
  });

  // Formulaire
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } =   useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      firstName: null,
      lastName: "",
      type: "INDIVIDUAL",
      enterpriseId: "",
      indice: undefined as number | undefined,
      phoneDialCode: DEFAULT_PHONE_DIAL_CODE,
      phone: "",
      email: "",
      contactPerson: "",
      notes: "",
      discountAmount: undefined as number | undefined,
      defaultPaymentMode: null as "EN_COMPTE" | "COMPTANT" | null | undefined,
      isActive: true,
      takesStarter: true,
      takesMain: true,
      takesDessert: true,
      proteinPreference: null as "FISH" | "MEAT" | "BOTH" | "VEGETARIAN" | null | undefined,
      prefersPremium: false,
      prefersVegetarian: false,
      allergenIds: [],
    },
  });

  const enterpriseIdForm = watch("enterpriseId");
  const indiceForm = watch("indice");
  const { data: enterpriseWithRules } = useQuery({
    queryKey: ["enterprises", enterpriseIdForm],
    queryFn: () => enterpriseService.getById(enterpriseIdForm!, true),
    enabled: !!enterpriseIdForm,
  });
  const displayDiscountRules = customer?.enterprise?.discountRules ?? enterpriseWithRules?.discountRules ?? [];

  // Remplir le formulaire avec les données du client si édition
  useEffect(() => {
    if (customer) {
      reset({
        firstName: customer.firstName ?? null,
        lastName: customer.lastName,
        type: customer.type,
        enterpriseId: customer.enterpriseId || "",
        indice: customer.indice ?? undefined,
        phoneDialCode: customer.phoneDialCode ?? DEFAULT_PHONE_DIAL_CODE,
        phone: customer.phone || "",
        email: customer.email || "",
        contactPerson: customer.contactPerson || "",
        notes: customer.notes || "",
        discountAmount: customer.discountAmount != null ? Number(customer.discountAmount) : undefined,
        defaultPaymentMode: (customer.defaultPaymentMode ?? null) as "EN_COMPTE" | "COMPTANT" | null | undefined,
        isActive: customer.isActive,
        takesStarter: customer.takesStarter ?? true,
        takesMain: customer.takesMain ?? true,
        takesDessert: customer.takesDessert ?? true,
        proteinPreference: (customer.proteinPreference ?? null) as CustomerFormData["proteinPreference"],
        prefersPremium: customer.prefersPremium ?? false,
        prefersVegetarian: customer.prefersVegetarian ?? false,
        allergenIds: customer.allergens?.map((ca) => ca.allergen.id) ?? [],
      });
    }
  }, [customer, reset]);

  // Préremplir le nom (lastName) si on arrive depuis la création de commande (suggestedName).
  // Pour les particuliers, on tente une séparation prénom/nom basique sur le premier espace.
  useEffect(() => {
    if (!isEditing) {
      const state = location.state as CustomerFormLocationState | undefined;
      const suggested = state?.suggestedName?.trim();
      if (suggested) {
        const tokens = suggested.split(/\s+/);
        if (tokens.length >= 2) {
          setValue("firstName", tokens[0]);
          setValue("lastName", tokens.slice(1).join(" "));
        } else {
          setValue("lastName", suggested);
        }
      }
    }
  }, [isEditing, location.state, setValue]);

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour créer
  const createMutation = useMutation({
    mutationFn: (data: CreateCustomerData) => customerService.create(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      const state = location.state as CustomerFormLocationState | undefined;
      if (state?.fromOrder) {
        success("Client créé. Redirection vers la prise de commande.");
        navigate("/orders/new", { state: { customerId: created.id } });
      } else {
        success("Client créé. Ajoutez au moins une adresse pour pouvoir passer des commandes.");
        navigate(`/customers/${created.id}`);
      }
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message || "Erreur lors de la création du client",
      );
    },
  });

  // Mutation pour mettre à jour
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateCustomerData>;
    }) => customerService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customers", id] });
      success("Client modifié avec succès");
      if (id) navigate(`/customers/${id}`);
      else navigate("/customers");
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la modification du client",
      );
    },
  });

  const onSubmit = (data: CustomerFormData) => {
    const customerData: CreateCustomerData = {
      firstName: data.firstName ?? null,
      lastName: data.lastName,
      type: data.type,
      enterpriseId: (data.enterpriseId as string) || undefined,
      indice: data.indice != null ? data.indice : undefined,
      phoneDialCode: data.phoneDialCode || DEFAULT_PHONE_DIAL_CODE,
      phone: data.phone || undefined,
      email: data.email || undefined,
      contactPerson: data.contactPerson || undefined,
      notes: data.notes || undefined,
      discountAmount: data.discountAmount != null ? data.discountAmount : undefined,
      defaultPaymentMode: data.defaultPaymentMode ?? undefined,
      isActive: data.isActive,
      takesStarter: data.takesStarter,
      takesMain: data.takesMain,
      takesDessert: data.takesDessert,
      proteinPreference: data.proteinPreference ?? undefined,
      prefersPremium: data.prefersPremium,
      prefersVegetarian: data.prefersVegetarian,
      allergenIds: data.allergenIds?.length ? data.allergenIds : undefined,
    };

    if (isEditing) {
      updateMutation.mutate({ id: id!, data: customerData });
    } else {
      createMutation.mutate(customerData);
    }
  };

  const createAddressMutation = useMutation({
    mutationFn: (data: CreateCustomerAddressData) =>
      customerService.createAddress(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", id] });
      success("Adresse ajoutée");
      setAddressDialogOpen(false);
      setEditingAddress(null);
      resetAddressForm();
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || "Erreur lors de l'ajout de l'adresse");
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: CreateCustomerAddressData }) =>
      customerService.updateAddress(id!, addressId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", id] });
      success("Adresse modifiée");
      setAddressDialogOpen(false);
      setEditingAddress(null);
      resetAddressForm();
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || "Erreur lors de la modification de l'adresse");
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => customerService.deleteAddress(id!, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", id] });
      success("Adresse supprimée");
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || "Erreur lors de la suppression de l'adresse");
    },
  });

  function resetAddressForm() {
    setAddressForm({
      label: "",
      address: "",
      postalCode: "",
      city: "",
      latitude: null,
      longitude: null,
      isDefault: false,
      isBilling: false,
    });
    setAddressFormErrors({});
  }

  function openAddAddress() {
    setEditingAddress(null);
    setAddressForm({
      label: "",
      address: "",
      postalCode: "",
      city: "",
      latitude: null,
      longitude: null,
      isDefault: (customer?.addresses?.length ?? 0) === 0,
      isBilling: false,
    });
    setAddressFormErrors({});
    setAddressDialogOpen(true);
  }

  function openEditAddress(addr: CustomerAddress) {
    setEditingAddress(addr);
    setAddressForm({
      label: addr.label ?? "",
      address: addr.address,
      postalCode: addr.postalCode ?? "",
      city: addr.city ?? "",
      latitude: null,
      longitude: null,
      isDefault: addr.isDefault,
      isBilling: addr.isBilling ?? false,
    });
    setAddressFormErrors({});
    setAddressDialogOpen(true);
  }

  function submitAddressForm() {
    const res = addressFormSchema.safeParse(addressForm);
    if (!res.success) {
      const errs: Partial<Record<keyof AddressFormData, string>> = {};
      res.error.flatten().fieldErrors &&
        Object.entries(res.error.flatten().fieldErrors).forEach(([k, v]) => {
          errs[k as keyof AddressFormData] = (v as string[])[0];
        });
      setAddressFormErrors(errs);
      return;
    }
    setAddressFormErrors({});
    const data: CreateCustomerAddressData = {
      label: res.data.label || null,
      address: res.data.address,
      postalCode: res.data.postalCode || null,
      city: res.data.city || null,
      latitude: res.data.latitude ?? null,
      longitude: res.data.longitude ?? null,
      isDefault: res.data.isDefault,
      isBilling: res.data.isBilling,
    };
    if (editingAddress) {
      updateAddressMutation.mutate({ addressId: editingAddress.id, data });
    } else {
      createAddressMutation.mutate(data);
    }
  }

  const isLoading = isEditing && customerLoading;

  const selectedType = watch("type");

  if (isLoading) {
    return (
      <LoadingSpinner
        message={isEditing ? "Chargement du client..." : "Chargement..."}
      />
    );
  }

  if (isEditing && customerError) {
    return <ErrorMessage message="Erreur lors du chargement du client." />;
  }

  const locationState = location.state as CustomerFormLocationState | undefined;
  const fromOrder = !isEditing && !!locationState?.fromOrder;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Titre */}
      <Typography variant="h2" sx={{ fontSize: "24px", fontWeight: 700 }}>
        {isEditing ? "Modifier le client" : "Nouveau client"}
      </Typography>

      {fromOrder && (
        <Alert severity="info" sx={{ mt: -1 }}>
          Vous créez ce client depuis la prise de commande. Après enregistrement, vous serez redirigé vers le formulaire de commande. Vous pourrez ajouter une adresse au client depuis sa fiche ou lors de la commande.
        </Alert>
      )}

      {/* Formulaire */}
      <Card>
        <Box
          component="form"
          onSubmit={handleSubmit((data: CustomerFormData) => onSubmit(data))}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Grid container spacing={3}>
            {/* Ligne 1 : type de client */}
            <Grid item xs={12}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.type} required>
                    <InputLabel>Type de client</InputLabel>
                    <Select {...field} label="Type de client">
                      {field.value === "INSTITUTION" && (
                        <MenuItem value="INSTITUTION" disabled>
                          Institution (type obsolète)
                        </MenuItem>
                      )}
                      <MenuItem value="COMPANY">Entreprise</MenuItem>
                      <MenuItem value="INDIVIDUAL">Particulier</MenuItem>
                    </Select>
                    {errors.type && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.type.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Ligne 2 : prénom et nom */}
            <Grid item xs={12} md={6}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="Prénom"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nom"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    required
                  />
                )}
              />
            </Grid>

            {/* Entreprise (si type entreprise ou institution) */}
            {(selectedType === "COMPANY" || selectedType === "INSTITUTION") && (
              <>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="enterpriseId"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Autocomplete
                        {...field}
                        options={enterprises}
                        getOptionLabel={(option) => option.name}
                        value={enterprises.find((e) => e.id === value) || null}
                        onChange={(_, newValue) => {
                          onChange(newValue ? newValue.id : "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Entreprise de rattachement"
                            fullWidth
                            error={!!errors.enterpriseId}
                            helperText={errors.enterpriseId?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                {enterpriseIdForm && (
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="indice"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Indice client"
                          type="number"
                          inputProps={{ min: 0 }}
                          fullWidth
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value === "" ? undefined : e.target.value)}
                          helperText="Détermine la règle de réduction appliquée (selon le tableau de l'entreprise)"
                        />
                      )}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Tableau de réduction entreprise (lecture seule, si entreprise avec règles) */}
            {(selectedType === "COMPANY" || selectedType === "INSTITUTION") && displayDiscountRules.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}>
                  Règles de réduction de l'entreprise (lecture seule)
                </Typography>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Libellé</TableCell>
                        <TableCell align="right">Indice min</TableCell>
                        <TableCell align="right">Indice max</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Réduction</TableCell>
                        <TableCell align="right">Reste à charge min (€)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayDiscountRules.map((r) => {
                        const raw = indiceForm;
                        const indiceNum =
                          raw == null || (typeof raw === "string" && raw === "")
                            ? null
                            : Number(raw);
                        const isActiveRow =
                          indiceNum != null && indiceNum >= r.indexMin && indiceNum <= r.indexMax;
                        return (
                        <TableRow
                          key={r.id}
                          sx={
                            isActiveRow
                              ? {
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(25, 118, 210, 0.2)"
                                      : "rgba(25, 118, 210, 0.08)",
                                  "&:hover": {
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "rgba(25, 118, 210, 0.28)"
                                        : "rgba(25, 118, 210, 0.12)",
                                  },
                                }
                              : undefined
                          }
                        >
                          <TableCell>{r.label}</TableCell>
                          <TableCell align="right">{r.indexMin}</TableCell>
                          <TableCell align="right">{r.indexMax}</TableCell>
                          <TableCell>{r.discountType === "FIXED" ? "Fixe (€)" : "Pourcentage (%)"}</TableCell>
                          <TableCell align="right">
                            {r.discountType === "FIXED" ? `${Number(r.discountValue).toFixed(2)} €` : `${Number(r.discountValue)} %`}
                          </TableCell>
                          <TableCell align="right">{Number(r.minChargeAmount).toFixed(2)} €</TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>
            )}

            {/* La zone de livraison se définit sur chaque adresse du client (voir section Adresses) */}

            {/* Personne de contact (pour institutions et entreprises) */}
            <Grid item xs={12} md={6}>
              <Controller
                name="contactPerson"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Personne de contact" fullWidth />
                )}
              />
            </Grid>

            {/* Téléphone */}
            <Grid item xs={12} md={6}>
              <Controller
                name="phoneDialCode"
                control={control}
                render={({ field: dialField }) => (
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field: phoneField }) => (
                      <PhoneField
                        dialCode={dialField.value}
                        phone={phoneField.value ?? ""}
                        onDialCodeChange={dialField.onChange}
                        onPhoneChange={phoneField.onChange}
                        size="medium"
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    fullWidth
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            {/* Préférences de commande */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "primary.main" }}>
                Préférences de commande
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Composition habituelle
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Controller
                      name="takesStarter"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={!!field.value} />}
                          label="Entrée"
                        />
                      )}
                    />
                    <Controller
                      name="takesMain"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={!!field.value} />}
                          label="Plat"
                        />
                      )}
                    />
                    <Controller
                      name="takesDessert"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} checked={!!field.value} />}
                          label="Dessert"
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ maxWidth: 320 }}>
                  <Controller
                    name="proteinPreference"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Préférence protéines</InputLabel>
                        <Select
                          {...field}
                          label="Préférence protéines"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value || null)}
                        >
                          <MenuItem value="">Non renseigné</MenuItem>
                          <MenuItem value="FISH">Poisson</MenuItem>
                          <MenuItem value="MEAT">Viande</MenuItem>
                          <MenuItem value="BOTH">Les deux</MenuItem>
                          <MenuItem value="VEGETARIAN">Végétarien</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Controller
                    name="prefersPremium"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={!!field.value} />}
                        label="Préfère les plats premium"
                      />
                    )}
                  />
                  <Controller
                    name="prefersVegetarian"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={!!field.value} />}
                        label="Préfère manger végétarien"
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Allergies / Intolérances (référentiel Allergènes) */}
            <Grid item xs={12}>
              <Controller
                name="allergenIds"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={allergens}
                    getOptionLabel={(option) => (option.icon ? `${option.icon} ${option.name}` : option.name)}
                    value={allergens.filter((a) => field.value.includes(a.id))}
                    onChange={(_, newValue) => field.onChange(newValue.map((a) => a.id))}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allergies / Intolérances"
                        placeholder="Sélectionner les allergènes du client..."
                        helperText="Une alerte s'affichera à la prise de commande si un produit contient un de ces allergènes."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          size="small"
                          label={option.icon ? `${option.icon} ${option.name}` : option.name}
                          color="warning"
                          variant="outlined"
                        />
                      ))
                    }
                  />
                )}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Informations supplémentaires (horaires de livraison préférés, instructions spéciales, etc.)"
                  />
                )}
              />
            </Grid>

            {/* Réduction 1ère commande du jour */}
            <Grid item xs={12} md={6}>
              <Controller
                name="discountAmount"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <TextField
                    {...field}
                    value={value ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "") onChange(undefined);
                      else onChange(v);
                    }}
                    label="Réduction (€) — 1ère commande du jour"
                    fullWidth
                    type="text"
                    inputMode="decimal"
                    placeholder="ex. 14,70"
                    helperText="Si renseigné, cette réduction s'applique sur la première commande du jour pour ce client."
                  />
                )}
              />
            </Grid>

            {/* Mode de paiement */}
            <Grid item xs={12} md={6}>
              <Controller
                name="defaultPaymentMode"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Mode de paiement</InputLabel>
                    <Select
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      label="Mode de paiement"
                    >
                      <MenuItem value="">
                        <em>Non renseigné</em>
                      </MenuItem>
                      <MenuItem value="EN_COMPTE">En compte</MenuItem>
                      <MenuItem value="COMPTANT">Comptant</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Statut actif */}
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Client actif"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button
              variant="text"
              onClick={() => navigate("/customers")}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEditing ? "Enregistrer les modifications" : "Créer le client"}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Section Adresses (édition uniquement) */}
      {isEditing && id && (
        <Card sx={{ mt: 3 }}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 600 }}>
                Adresses
              </Typography>
              <Button
                variant="outline"
                size="small"
                startIcon={<Add />}
                onClick={openAddAddress}
              >
                Ajouter une adresse
              </Button>
            </Box>
            {!customer?.addresses?.length ? (
              <Typography color="text.secondary" variant="body2">
                Aucune adresse. Ajoutez au moins une adresse pour pouvoir passer des commandes.
              </Typography>
            ) : (
              <List dense disablePadding>
                {(customer.addresses ?? []).map((addr) => (
                  <ListItem
                    key={addr.id}
                    secondaryAction={
                      <Box component="span" sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEditAddress(addr)} aria-label="Modifier">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm("Supprimer cette adresse ?")) {
                              deleteAddressMutation.mutate(addr.id);
                            }
                          }}
                          disabled={(customer.addresses?.length ?? 0) <= 1}
                          aria-label="Supprimer"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                    sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {addr.label && (
                            <Typography component="span" fontWeight={600}>
                              {addr.label}
                            </Typography>
                          )}
                          {addr.isDefault && (
                            <Chip label="Par défaut" size="small" color="primary" variant="outlined" />
                          )}
                          {!isAddressDeliverable(addr) && (
                            <Chip label="Hors zone" size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={[
                        addr.address,
                        formatLocationLine(addr.postalCode, addr.city),
                        addr.zone?.name ? `Zone : ${addr.zone.name}` : "Hors zone de livraison",
                      ].filter(Boolean).join(" · ")}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Card>
      )}

      {/* Dialog Ajouter/Modifier adresse */}
      <Dialog open={addressDialogOpen} onClose={() => { setAddressDialogOpen(false); setEditingAddress(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAddress ? "Modifier l'adresse" : "Ajouter une adresse"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                La zone de livraison est calculée automatiquement. Une adresse hors zone ne permet
                que le retrait sur place.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Libellé (ex. Domicile, Bureau)"
                value={addressForm.label}
                onChange={(e) => setAddressForm((f) => ({ ...f, label: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <AddressAutocomplete
                address={addressForm.address}
                postalCode={addressForm.postalCode ?? ""}
                city={addressForm.city ?? ""}
                onAddressChange={({ address, postalCode, city, latitude, longitude }) =>
                  setAddressForm((f) => ({
                    ...f,
                    address,
                    postalCode: postalCode ?? "",
                    city: city ?? "",
                    latitude: latitude ?? null,
                    longitude: longitude ?? null,
                  }))
                }
                label="Adresse"
                error={!!addressFormErrors.address}
                helperText={addressFormErrors.address}
                required
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code postal"
                value={addressForm.postalCode}
                onChange={(e) => setAddressForm((f) => ({ ...f, postalCode: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ville"
                value={addressForm.city}
                onChange={(e) => setAddressForm((f) => ({ ...f, city: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addressForm.isDefault}
                    onChange={(e) => setAddressForm((f) => ({ ...f, isDefault: e.target.checked }))}
                  />
                }
                label="Adresse par défaut"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addressForm.isBilling}
                    onChange={(e) => setAddressForm((f) => ({ ...f, isBilling: e.target.checked }))}
                  />
                }
                label="Adresse de facturation"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => { setAddressDialogOpen(false); setEditingAddress(null); }}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={submitAddressForm}
            loading={createAddressMutation.isPending || updateAddressMutation.isPending}
          >
            {editingAddress ? "Enregistrer" : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
